import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import dataRoomApi from '../../dataRoomApi';
import useQueryParams from 'hooks/useQueryParams';
import usePageParams from 'hooks/usePageParams';
import { DataRoomNdaSigner } from '../../models/DataRoomNDA';
import DataRoomNdasTable from './components/DataRoomNdasTable';
import DataRoomNdasEmptyScreen from './components/DataRoomNdasEmptyScreen';
import DataRoomContext from '../../providers/DataRoom/DataRoom.context';
import DataRoomNdaTableProvider from './providers/DataRoomNdaTableProvider/DataRoomNdaTableProvider.provider';
import { MOBILE_NDA_SELECTOR } from '../../providers/Onboarding/data';
import HelmetWrapper from 'components/HelmetWrapper';
import { Button } from 'ncoded-component-library';
import PlusIcon from 'icons/Plus.icon';
import AddNdaModal from './components/AddNdaModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import NdaListModal from 'components/NdaListModal';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { NDATemplate } from 'models/Nda';

import './DataRoomNdas.styles.scss';

const LIMIT = '10';

type DataRoomNdasProps = {
  className?: string;
};

const DataRoomNdas: React.FC<DataRoomNdasProps> = (props) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [items, setItems] = useState<DataRoomNdaSigner[]>([]);

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const {
    dataRoom: { _id, name },
  } = useContext(DataRoomContext);

  const [addNdaModal, addNdaModalRef] = useCallbackRef<ModalRef>();
  const [ndaListModal, ndaListModalRef] = useCallbackRef<ModalRef>();

  const classes = classNames('data-room-ndas', 'page', className);

  const { params } = useQueryParams();

  const page = usePageParams();
  const { dataRoomNdaId, sort } = params;

  const { t } = useTranslation();

  const isEmpty = useMemo(
    () => !isLoading && !items?.length,
    [isLoading, items?.length],
  );

  const updateSigner = useCallback((signer: Partial<DataRoomNdaSigner>) => {
    setItems((prev) =>
      prev.map((s) => (s._id !== signer._id ? s : { ...s, ...signer })),
    );
  }, []);

  const onUploadNda = useCallback(
    async ({ templateId, name }: NDATemplate) => {
      await dataRoomApi.nda.linkNdaWithDataRoom(_id, templateId, name);
    },
    [_id],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const {
          data: { items, totalPages },
        } = await dataRoomApi.nda.getNdaSigners(_id, {
          $page: page,
          $limit: LIMIT,
          $sort: sort,
          dataRoomNdaId,
        });

        setItems(items);
        setTotalPages(totalPages);
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [_id, page, sort, dataRoomNdaId]);

  return (
    <div className={classes}>
      <HelmetWrapper title={`${t('ndas')} - ${name}`} />
      <h2 id={MOBILE_NDA_SELECTOR}>{t('ndas')}</h2>

      <div className="actions">
        {isSuperAdmin && (
          <>
            <Button variant="outline" onClick={ndaListModal?.open}>
              {t('connectNda')}
            </Button>
            <NdaListModal
              ref={ndaListModalRef}
              description={t('connectNondisclosureAgrementWithDataRoom')}
              onSelectNda={onUploadNda}
            />
          </>
        )}

        <Button icon={PlusIcon} onClick={addNdaModal?.open}>
          {t('addNda')}
        </Button>
        <AddNdaModal ref={addNdaModalRef} />
      </div>

      {isEmpty ? (
        <DataRoomNdasEmptyScreen />
      ) : (
        <DataRoomNdaTableProvider signers={items} updateSigner={updateSigner}>
          <DataRoomNdasTable
            isLoading={isLoading}
            totalPages={totalPages}
            items={items}
          />
        </DataRoomNdaTableProvider>
      )}
    </div>
  );
};

export default DataRoomNdas;
